import experiment from 'experiment';
import type { CompRef } from 'types/documentServices';
import type { EditorAPI } from '#packages/editorAPI';
import type { DragHandlerHooks } from '#packages/addPanelInfra';

import { isSlotOpenerEnabled, reportSlotOpenedBI } from '../utils';
import type { SlotOpenerScope } from '../scope';
import { debouncedSetSlot, isMouseOverTheLine } from './utils';
import { getLineRectV3 } from '../components/utils';

export const createSlotOpenerDragPluginAddPanel = (scope: SlotOpenerScope) => {
  return {
    init: (editorAPI: EditorAPI, hooks: DragHandlerHooks) => {
      const { slotOpenerPrivateApi, store } = scope;

      if (!isSlotOpenerEnabled(editorAPI)) {
        return;
      }

      let prevAttachCandidate: CompRef | null = null;

      hooks.onAttachCandidateFound.tap(({ candidate, dragBoxStageLayout }) => {
        if (
          editorAPI.sections.isHeader(candidate) ||
          editorAPI.sections.isHeader(
            editorAPI.sections.getClosestSectionLike(candidate),
          )
        ) {
          return;
        }

        if (prevAttachCandidate?.id === candidate.id) {
          return;
        }

        prevAttachCandidate = candidate;
        const allSlots = slotOpenerPrivateApi.getAllSlotsInContainer(
          candidate,
          dragBoxStageLayout,
        );
        store.setAllSlotsInContainer(allSlots);
      });

      let prevMouseY = 0;
      hooks.onDragOutsideAddPanelArea.tap(
        ({ mouseCoordinates, dragBoxStageLayout }) => {
          if (mouseCoordinates.pageY === prevMouseY) {
            return;
          }

          const dragDirection =
            mouseCoordinates.pageY > prevMouseY ? 'down' : 'up';
          const slot = slotOpenerPrivateApi.findAvailableSlot(
            store.getAllSlotsInContainer(),
            dragBoxStageLayout,
            dragDirection,
            mouseCoordinates,
          );

          if (experiment.isOpen('se_slotOpenerV2')) {
            const prevSlot = store.getAvailableSlot();
            if (prevSlot && prevSlot.aboveComponent !== slot?.aboveComponent) {
              store.setAvailableSlot(slot);
            }

            if (!slot) {
              debouncedSetSlot.cancel();
            } else {
              debouncedSetSlot(scope, slot);
            }
          } else {
            store.setAvailableSlot(slot);
          }

          if (experiment.isOpen('se_slotOpenerV3') && slot) {
            const lineLayout = getLineRectV3(slot, store.getShouldOpenSlot());

            store.setShouldOpenSlot(
              isMouseOverTheLine(mouseCoordinates, lineLayout),
            );
          }

          prevMouseY = mouseCoordinates.pageY;
        },
      );

      hooks.onBeforeItemDrop.tap(({ container, itemLayout }) => {
        const slot = store.getAvailableSlot();

        const shouldOpenSlot = experiment.isOpen('se_slotOpenerV3')
          ? store.getShouldOpenSlot()
          : true;

        if (!slot || !container || !shouldOpenSlot) {
          return;
        }

        slotOpenerPrivateApi.openSlot(slot, container, itemLayout);
      });

      hooks.onAfterItemDrop.tap(({ compRef }) => {
        const slot = store.getAvailableSlot();

        if (!slot || !compRef) {
          return;
        }

        reportSlotOpenedBI(editorAPI, compRef, 'add_panel');

        store.setAvailableSlot(null);
        store.setShouldOpenSlot(false);
        prevAttachCandidate = null;
      });
    },
  };
};
